/**
 * TagPill — pill colorat pentru afișare tag (read-only sau cu X pt detach).
 * Paletă aliniată cu codex-v6: mint/cyan/purple/amber/rose/blue/indigo/yellow/neutral.
 */
import { useTranslation } from 'react-i18next';

import type { TagColor } from '@/api/tags';

interface Props {
  label: string;
  color: TagColor | null;
  size?: 'sm' | 'md';
  onRemove?: () => void;
  onClick?: () => void;
  interactive?: boolean;
}

const COLOR_FG: Record<TagColor, string> = {
  mint: 'var(--c-mint)',
  cyan: 'var(--c-cyan)',
  blue: 'var(--c-blue)',
  purple: 'var(--c-purple)',
  amber: 'var(--c-amber)',
  rose: 'var(--c-rose)',
  yellow: 'var(--c-yellow)',
  indigo: 'var(--c-indigo)',
  neutral: 'var(--text-muted)',
};
const COLOR_BG: Record<TagColor, string> = {
  mint: 'var(--bg-mint)',
  cyan: 'var(--bg-cyan)',
  blue: 'var(--bg-blue)',
  purple: 'var(--bg-purple)',
  amber: 'var(--bg-amber)',
  rose: 'var(--bg-rose)',
  yellow: 'var(--bg-yellow)',
  indigo: 'var(--bg-indigo)',
  neutral: 'var(--bg-surface)',
};

export default function TagPill({
  label,
  color,
  size = 'md',
  onRemove,
  onClick,
  interactive,
}: Props) {
  const { t } = useTranslation();
  const c: TagColor = color ?? 'neutral';
  const fg = COLOR_FG[c];
  const bg = COLOR_BG[c];
  const isSmall = size === 'sm';
  const padding = isSmall ? '2px 7px' : '3px 10px';
  const fontSize = isSmall ? 10 : 11;

  const base = (
    <span
      className="inline-flex items-center gap-1.5 font-mono rounded-full transition-colors"
      style={{
        padding: onRemove ? `${padding.split(' ')[0]} 4px ${padding.split(' ')[0]} ${padding.split(' ')[1]}` : padding,
        fontSize,
        color: fg,
        background: bg,
        border: `1px solid ${fg}`,
        letterSpacing: '0.02em',
        fontWeight: 500,
        cursor: interactive || onClick ? 'pointer' : 'default',
      }}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
    >
      <span>{label}</span>
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="w-3.5 h-3.5 rounded-full grid place-items-center"
          style={{ color: fg }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = fg;
            e.currentTarget.style.color = 'var(--bg-elevated)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = fg;
          }}
          aria-label={t('vault_ui.tag_picker.remove', { label })}
        >
          <svg
            viewBox="0 0 24 24"
            width="9"
            height="9"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <path d="M6 6l12 12M6 18L18 6" />
          </svg>
        </button>
      )}
    </span>
  );
  return base;
}
